import type { Notifier } from "../NotificationScheduler.js";
import { tr } from "../../../lib/tr.js";

/**
 * Android (Capacitor): the OS owns reminder timing. reconcile() hands the
 * desired fire set to scheduleAll, which diffs it against the OS's pending set
 * (unchanged reminders are left alone — no churn) and lets AlarmManager fire
 * them with the app backgrounded or killed. The plugin reschedules everything
 * after a device reboot. The import is a real bundled (code-split) module —
 * browser/Tauri builds never fetch the chunk.
 */

const CHANNEL_ID = "reminders";
/** Action-button group attached to every reminder; handled by useNotificationActions. */
export const REMINDER_ACTION_TYPE = "reminder";
export const SNOOZE_ACTIONS = { snooze10: 10, snooze60: 60 } as const;
/** Android caps pending alarms per app (~500); stay far under it. Only the
 *  soonest MAX_SCHEDULED fires are handed to the OS — surfaced in diagnostics. */
export const MAX_SCHEDULED = 64;
/** Reserved id for the Settings "test notification" — sits above the
 *  minutes-since-epoch range reconcile uses, so scheduleAll leaves it alone. */
const TEST_ID = 2_000_000_000;

// TRAP: LocalNotifications is a registerPlugin() proxy — it must never be a
// promise's resolution value (the engine's thenable probe becomes a native
// `.then()` bridge call, which throws). Wrap it in an object instead.
async function ln() {
  const mod = await import("@capacitor/local-notifications");
  return { LN: mod.LocalNotifications };
}

type LN = Awaited<ReturnType<typeof ln>>["LN"];

/** Opt-in on-device diagnostics (`localStorage.notifyDebug="1"`), mirroring the
 *  app's gsyncDebug/inkDebug convention. */
function debug(...args: unknown[]): void {
  try {
    if (localStorage.getItem("notifyDebug") === "1") console.log("[notify]", ...args);
  } catch { /* localStorage may be unavailable */ }
}

/** One-time channel + action-type registration; memoized only on SUCCESS so a
 *  transient failure can be retried on the next reconcile. */
let setup: Promise<void> | null = null;
function ensureSetup(LN: LN): Promise<void> {
  setup ??= (async () => {
    await LN.createChannel({
      id: CHANNEL_ID,
      name: tr("notification.channelName"),
      description: tr("notification.channelDescription"),
      importance: 4,
    });
    await LN.registerActionTypes({
      types: [{
        id: REMINDER_ACTION_TYPE,
        actions: [
          { id: "snooze10", title: tr("notification.snooze10") },
          { id: "snooze60", title: tr("notification.snooze60") },
        ],
      }],
    });
  })().catch((e) => { setup = null; throw e; });
  return setup;
}

// Show the system permission dialog at most once per session. Permission STATUS
// is still re-read every call so a user who enables notifications in Android
// settings after an earlier denial takes effect without restarting the app.
let requestedOnce = false;

/**
 * Resolve a ready-to-use plugin handle, or null if notifications can't fire.
 * NOT memoized on failure (the old `prepare()` was): a cached "denied" survived
 * a later system-settings grant and silently suppressed every reminder until the
 * app was force-restarted.
 */
async function ensureReady(): Promise<{ LN: LN } | null> {
  try {
    const { LN } = await ln();
    let perm = await LN.checkPermissions();
    if (perm.display !== "granted" && !requestedOnce) {
      requestedOnce = true;
      perm = await LN.requestPermissions();
    }
    if (perm.display !== "granted") {
      debug("permission not granted:", perm.display);
      return null;
    }
    await ensureSetup(LN);
    return { LN };
  } catch (e) {
    debug("ensureReady failed:", e);
    return null;
  }
}

/** Stable per-minute id: minutes since epoch (fits int32 for millennia). */
const idFor = (atMs: number) => Math.floor(atMs / 60_000);

/**
 * Per-day notification icons (generated by apps/mobile/scripts/gen-day-icons.mjs):
 * the status-bar small icon is a white day-number silhouette, the body large icon
 * a colour calendar card with the day number — mirroring the desktop favicon. The
 * day-of-month comes from the fire time, so the icon reads "today" when it shows.
 */
function dayIcons(atMs: number): { smallIcon: string; largeIcon: string } {
  const day = new Date(atMs).getDate();
  return { smallIcon: `ic_stat_day_${day}`, largeIcon: `ic_cal_day_${day}` };
}

/** What the OS currently has scheduled for us, for the on-screen diagnostics. */
export async function capacitorDiagnostics(): Promise<{
  permission: string;
  channelImportance: number | null;
  pending: Array<{ title: string; at: string | null }>;
}> {
  const { LN } = await ln();
  const perm = await LN.checkPermissions();
  let channelImportance: number | null = null;
  try {
    const chans = await LN.listChannels();
    channelImportance = chans.channels.find((c) => c.id === CHANNEL_ID)?.importance ?? null;
  } catch { /* listChannels unsupported on some OEMs */ }
  const pending = await LN.getPending();
  const at = (s: PendingSchedule): string | null => {
    const v = s?.schedule?.at;
    if (!v) return null;
    const d = typeof v === "string" ? new Date(v) : v;
    return d instanceof Date && !isNaN(d.getTime()) ? d.toLocaleString() : null;
  };
  return {
    permission: perm.display,
    channelImportance,
    pending: pending.notifications
      .filter((p) => p.id !== TEST_ID)
      .map((p) => ({ title: p.title, at: at(p as PendingSchedule) })),
  };
}
type PendingSchedule = { schedule?: { at?: string | Date } };

export const capacitorNotifier: Notifier = {
  async send({ title, body }) {
    const ready = await ensureReady();
    if (!ready) return;
    await ready.LN.schedule({
      notifications: [{ id: Date.now() % 2_147_483_647, title: title || tr("notification.fallbackTitle"), body, channelId: CHANNEL_ID, ...dayIcons(Date.now()) }],
    });
  },

  async sendTest({ atMs }) {
    const ready = await ensureReady();
    if (!ready) {
      throw new Error(tr("notification.notPermitted"));
    }
    const now = Date.now();
    await ready.LN.schedule({
      notifications: [{
        id: atMs ? TEST_ID : now % 2_147_483_647,
        title: tr("notification.testTitle"),
        body: atMs
          ? tr("notification.testBackground")
          : tr("notification.testImmediate"),
        channelId: CHANNEL_ID,
        ...dayIcons(atMs || now),
        ...(atMs ? { schedule: { at: new Date(Math.max(atMs, now + 1_000)), allowWhileIdle: true } } : {}),
      }],
    });
    debug(atMs ? "scheduled test for" : "sent test now", atMs ? new Date(atMs).toISOString() : "");
    return { background: !!atMs };
  },

  async scheduleAll(fires) {
    const ready = await ensureReady();
    if (!ready) { debug("scheduleAll: not ready (permission?)"); return; }
    const { LN } = ready;
    const now = Date.now();

    const desired = new Map(
      fires
        .filter((f) => f.atMs > now - 60_000) // this minute still fires; older are gone
        .slice(0, MAX_SCHEDULED)
        .map((f) => [idFor(f.atMs), f] as const),
    );
    debug("scheduleAll: fires in =", fires.length, "→ desired =", desired.size);

    // Leave already-correct pending entries untouched; cancel stale/changed ones.
    // Guarded: a getPending/cancel failure must NOT abort the schedule below.
    try {
      const pending = await LN.getPending();
      const stale: Array<{ id: number }> = [];
      for (const p of pending.notifications) {
        if (p.id === TEST_ID) continue; // one-off diagnostic, not reconcile-managed
        const want = desired.get(p.id);
        if (want && p.title === want.payload.title && p.body === want.payload.body) desired.delete(p.id);
        else stale.push({ id: p.id });
      }
      if (stale.length > 0) await LN.cancel({ notifications: stale });
      debug("scheduleAll: pending =", pending.notifications.length, "stale cancelled =", stale.length);
    } catch (e) {
      debug("scheduleAll: pending/cancel failed (continuing):", e);
    }

    if (desired.size === 0) return;

    const at = (atMs: number) => new Date(Math.max(atMs, now + 1_000)); // boundary → future
    const base = [...desired.values()].map((f) => ({
      id: idFor(f.atMs),
      title: f.payload.title || tr("notification.fallbackTitle"),
      body: f.payload.body,
      channelId: CHANNEL_ID,
      ...dayIcons(f.atMs),
      // A bucket already at the boundary still gets a future fire time.
      schedule: { at: at(f.atMs), allowWhileIdle: true },
    }));

    try {
      // Preferred: with Snooze action buttons + taskIds payload.
      await LN.schedule({
        notifications: base.map((n, i) => ({
          ...n,
          actionTypeId: REMINDER_ACTION_TYPE,
          extra: { taskIds: [...desired.values()][i]!.payload.taskIds ?? [] },
        })),
      });
      debug("scheduled", base.length, "reminder(s)");
    } catch (e) {
      // The Snooze action type / extra payload is the one delta the Settings
      // "test notification" never exercises. If it's what a given device rejects,
      // fall back to plain reminders so the user still gets notified (no buttons).
      debug("rich schedule failed, retrying plain:", e);
      try {
        await LN.schedule({ notifications: base });
        debug("scheduled", base.length, "plain reminder(s) (no action buttons)");
      } catch (e2) {
        debug("plain schedule also failed:", e2);
      }
    }
  },
};
